/**
 * Geometry for `canvasDrawing` ink strokes.
 *
 * Points travel as a flat `[x0, y0, x1, y1, …]` list. While drawing they are
 * absolute flow coordinates; on commit they are rebased to node-local
 * coordinates inside a padded bounding box, the way connectors rebase their
 * endpoints.
 *
 * The sampling and simplification thresholds are screen pixels — callers
 * divide by the live zoom so a stroke drawn at 4x is no coarser than one drawn
 * at 1x.
 */

/** Padding around the stroke bounding box so thick caps are never clipped. */
export const DRAWING_NODE_PAD = 16;

/** Screen-space distance below which a pointer sample is dropped as a repeat. */
export const DRAWING_MIN_SAMPLE_PX = 1.5;

/** Screen-space deviation a simplified stroke may introduce. */
export const DRAWING_SIMPLIFY_PX = 0.6;

/** How far a new sample is pulled toward the previous one (0 = raw, 1 = frozen). */
const STREAMLINE = 0.4;

/** Distance from `(px, py)` to the segment `(x1, y1) → (x2, y2)`. */
export function pointToSegmentDistance(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;
  const t =
    lengthSquared === 0
      ? 0
      : Math.max(
          0,
          Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lengthSquared),
        );
  return Math.hypot(px - (x1 + dx * t), py - (y1 + dy * t));
}

/**
 * Append a raw pointer sample, dropping it when it lands within `minDistance`
 * of the last one and easing it toward that point so hand jitter doesn't show.
 */
export function appendDrawingSample(
  points: number[],
  x: number,
  y: number,
  minDistance: number,
): void {
  if (points.length < 2) {
    points.push(x, y);
    return;
  }

  const px = points[points.length - 2];
  const py = points[points.length - 1];
  if (Math.hypot(x - px, y - py) < minDistance) return;

  points.push(
    px + (x - px) * (1 - STREAMLINE),
    py + (y - py) * (1 - STREAMLINE),
  );
}

/**
 * Ramer–Douglas–Peucker: drop samples no further than `tolerance` from the
 * chord they sit on. A held pen emits samples faster than the curve changes,
 * and every one of them is synced to every other player, so a finished stroke
 * is thinned before it is committed. Endpoints always survive.
 */
export function simplifyDrawingPoints(
  points: number[],
  tolerance: number,
): number[] {
  const count = points.length >> 1;
  if (count < 3 || tolerance <= 0) return points;

  const keep = new Uint8Array(count);
  keep[0] = 1;
  keep[count - 1] = 1;

  const stack: [number, number][] = [[0, count - 1]];
  while (stack.length > 0) {
    const [first, last] = stack.pop()!;
    if (last <= first + 1) continue;

    let farthest = -1;
    let maxDistance = tolerance;
    for (let i = first + 1; i < last; i++) {
      const distance = pointToSegmentDistance(
        points[i * 2],
        points[i * 2 + 1],
        points[first * 2],
        points[first * 2 + 1],
        points[last * 2],
        points[last * 2 + 1],
      );
      if (distance > maxDistance) {
        maxDistance = distance;
        farthest = i;
      }
    }
    if (farthest < 0) continue;

    keep[farthest] = 1;
    stack.push([first, farthest], [farthest, last]);
  }

  const simplified: number[] = [];
  for (let i = 0; i < count; i++) {
    if (keep[i]) simplified.push(points[i * 2], points[i * 2 + 1]);
  }
  return simplified;
}

/**
 * SVG path through the samples: a quadratic segment per point whose control
 * point is the sample itself and whose end is the midpoint to the next one —
 * the classic pencil curve, C1-continuous with no spline solve.
 */
export function drawingStrokePath(points: number[]): string {
  if (points.length < 2) return "";

  // A lone sample still has to paint: a zero-length line under a round cap is
  // the dot a tap leaves behind.
  if (points.length === 2) {
    return `M ${points[0]} ${points[1]} L ${points[0]} ${points[1]}`;
  }

  if (points.length < 6) {
    return `M ${points[0]} ${points[1]} L ${points[2]} ${points[3]}`;
  }

  const commands = [`M ${points[0]} ${points[1]}`];
  for (let i = 2; i < points.length - 2; i += 2) {
    const cx = points[i];
    const cy = points[i + 1];
    commands.push(
      `Q ${cx} ${cy} ${(cx + points[i + 2]) / 2} ${(cy + points[i + 3]) / 2}`,
    );
  }
  commands.push(`L ${points[points.length - 2]} ${points[points.length - 1]}`);
  return commands.join(" ");
}

export interface DrawingNodeGeometry {
  position: { x: number; y: number };
  width: number;
  height: number;
  /** The same stroke, rebased into the padded box. */
  localPoints: number[];
}

/** Padded bounding box of absolute points, and the same points rebased into it. */
export function drawingNodeGeometry(points: number[]): DrawingNodeGeometry {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < points.length; i += 2) {
    if (points[i] < minX) minX = points[i];
    if (points[i] > maxX) maxX = points[i];
    if (points[i + 1] < minY) minY = points[i + 1];
    if (points[i + 1] > maxY) maxY = points[i + 1];
  }

  return {
    position: { x: minX - DRAWING_NODE_PAD, y: minY - DRAWING_NODE_PAD },
    width: maxX - minX + DRAWING_NODE_PAD * 2,
    height: maxY - minY + DRAWING_NODE_PAD * 2,
    localPoints: points.map((value, i) =>
      i % 2 === 0
        ? value - minX + DRAWING_NODE_PAD
        : value - minY + DRAWING_NODE_PAD,
    ),
  };
}

/** Read a persisted `points` value, tolerating partial / agent-authored data. */
export function readDrawingPoints(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const points = value.filter(
    (n): n is number => typeof n === "number" && Number.isFinite(n),
  );
  return points.length % 2 === 0 ? points : points.slice(0, -1);
}
